import { useRef } from "react";
import { api } from "../../lib/api";
import { NorthArrow, PLAN_TEMPLATES } from "./planTemplates";

const W = 400;
const H = 300;
const DIRS = ["北", "北東", "東", "南東", "南", "南西", "西", "北西"];
export const directionIndex = (angle) => Math.round((((angle ?? -90) + 90) % 360 + 360) % 360 / 45) % 8;
export const directionJa = (angle) => DIRS[directionIndex(angle)];
export const planImageUrl = (id) => `${api.defaults.baseURL}/staff/surveys/plan-images/${id}`;

const Marker = ({ item, index, color, selected }) => {
  const cx = item.x * W;
  const cy = item.y * H;
  const rad = ((item.angle ?? -90) * Math.PI) / 180;
  const ex = cx + Math.cos(rad) * 40;
  const ey = cy + Math.sin(rad) * 40;
  const head = [0.45, -0.45].map((d) => `${ex - Math.cos(rad + d) * 12},${ey - Math.sin(rad + d) * 12}`).join(" ");
  return (
    <g data-marker={index} data-testid={`plan-marker-${index}`} style={{ cursor: "pointer" }}>
      <path d={`M${cx} ${cy} L${cx + Math.cos(rad - 0.5) * 46} ${cy + Math.sin(rad - 0.5) * 46} A46 46 0 0 1 ${cx + Math.cos(rad + 0.5) * 46} ${cy + Math.sin(rad + 0.5) * 46} Z`} fill={color} opacity="0.18" />
      <line x1={cx} y1={cy} x2={ex} y2={ey} stroke={color} strokeWidth="4" strokeLinecap="round" />
      <polygon points={`${ex},${ey} ${head}`} fill={color} />
      <circle cx={cx} cy={cy} r="26" fill="transparent" />
      <circle cx={cx} cy={cy} r="14" fill={color} stroke={selected ? "#fff" : "#0b0d12"} strokeWidth={selected ? 3 : 2} />
      <text x={cx} y={cy + 5} textAnchor="middle" fontSize="14" fontWeight="700" fill="#0b0d12">{index + 1}</text>
    </g>
  );
};

export const SitePlan = ({ plan, items, colorFor, selected, editable, onSelect, onPlace, onRotate }) => {
  const ref = useRef(null);
  const gesture = useRef(null);
  const toPt = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) };
  };
  const down = (e) => {
    const hit = e.target.closest("[data-marker]");
    gesture.current = hit ? { index: Number(hit.dataset.marker), moved: false } : { bg: toPt(e) };
    if (hit && editable) e.currentTarget.setPointerCapture(e.pointerId);
  };
  const move = (e) => {
    const g = gesture.current;
    if (!editable || !g || g.index === undefined) return;
    const it = items[g.index];
    const p = toPt(e);
    const dx = (p.x - it.x) * W;
    const dy = (p.y - it.y) * H;
    if (Math.hypot(dx, dy) < 16) return;
    g.moved = true;
    onRotate(g.index, Math.round((Math.atan2(dy, dx) * 180) / Math.PI));
  };
  const up = () => {
    const g = gesture.current;
    gesture.current = null;
    if (!g) return;
    if (g.index !== undefined && !g.moved) onSelect?.(g.index);
    else if (g.bg && editable) onPlace?.(g.bg);
  };
  const template = PLAN_TEMPLATES.find((t) => t.id === plan?.template);
  return (
    <div ref={ref} data-testid="site-plan" className="relative aspect-[4/3] w-full select-none overflow-hidden border border-line bg-[#11151d]">
      {plan?.kind === "image" && plan.image_id && <img data-testid="site-plan-image" src={planImageUrl(plan.image_id)} alt="平面図" className="absolute inset-0 h-full w-full object-contain" draggable={false} />}
      <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 h-full w-full" style={{ touchAction: editable ? "none" : "manipulation" }} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => (gesture.current = null)}>
        {plan?.kind === "template" && template?.svg}
        <NorthArrow />
        {items.map((it, i) => (it.x == null ? null : <Marker key={it.id} item={it} index={i} color={colorFor(it, i)} selected={selected === i} />))}
      </svg>
    </div>
  );
};
